import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Mail, Instagram } from "lucide-react";
import { useSettings } from "../context/SettingsContext";

const Contact = () => {
  const { settings } = useSettings();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email, and message are required.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Thank you — your note has reached the studio.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="px-6 md:px-12 lg:px-20 py-20 md:py-28" data-testid="page-contact">
      <div className="grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="label-eyebrow mb-4">Contact</div>
          <h1 className="font-serif-display text-5xl md:text-6xl text-[#1A1F1B] leading-[1]">
            Send a note.
          </h1>
          {settings.contact_intro && (
            <p className="mt-6 text-[#4E5C50] max-w-md leading-relaxed" data-testid="contact-intro">
              {settings.contact_intro}
            </p>
          )}

          <div className="mt-12 space-y-4 text-sm">
            {settings.contact_email && (
              <a
                href={`mailto:${settings.contact_email}`}
                className="flex items-center gap-3 text-[#3B4A3F] hover:text-[#A39171] transition-colors"
                data-testid="contact-email-link"
              >
                <Mail size={16} strokeWidth={1.4} /> {settings.contact_email}
              </a>
            )}
            {settings.contact_instagram && (
              <a
                href={`https://instagram.com/${settings.contact_instagram}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 text-[#3B4A3F] hover:text-[#A39171] transition-colors"
                data-testid="contact-instagram-link"
              >
                <Instagram size={16} strokeWidth={1.4} /> @{settings.contact_instagram}
              </a>
            )}
          </div>
        </div>

        <form
          onSubmit={submit}
          className="md:col-span-7 space-y-8"
          data-testid="contact-form"
        >
          <div className="grid sm:grid-cols-2 gap-8">
            <div>
              <label className="label-eyebrow">Name</label>
              <input
                className="earth"
                name="name"
                value={form.name}
                onChange={onChange}
                required
                data-testid="contact-input-name"
              />
            </div>
            <div>
              <label className="label-eyebrow">Email</label>
              <input
                className="earth"
                type="email"
                name="email"
                value={form.email}
                onChange={onChange}
                required
                data-testid="contact-input-email"
              />
            </div>
          </div>
          <div>
            <label className="label-eyebrow">Subject</label>
            <input
              className="earth"
              name="subject"
              value={form.subject}
              onChange={onChange}
              data-testid="contact-input-subject"
            />
          </div>
          <div>
            <label className="label-eyebrow">Message</label>
            <textarea
              className="earth"
              name="message"
              rows={6}
              value={form.message}
              onChange={onChange}
              required
              data-testid="contact-input-message"
            />
          </div>
          <button
            type="submit"
            disabled={sending}
            className="btn-primary disabled:opacity-50"
            data-testid="contact-submit"
          >
            {sending ? "Sending…" : "Send Message"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Contact;
